import { useCallback, useEffect, useRef, useState } from 'react';

import { useDismissOnOutside } from './use-dismiss-on-outside';

// A stable ref so the hook's dependency array does not change identity
// every render while the panel is closed.
const NOT_OPEN = { current: null };
import { useFavoritesV1, useExecuteFavoriteV1, useDeleteFavoriteV1 } from '../api/hooks/useFavorites';
import { ConfirmDialog } from '../design-system/components/ConfirmDialog';
import type { TaskFavorite } from '../api/v1';
import { cleanModeLabel, cleanModeOfRequest } from '../screens/Tasks/clean-modes';
import { PriorityBadge } from '../screens/Tasks/TaskPriority';
import { favoriteRunMessage, useCanSetHighPriority } from '../screens/Tasks/task-priority';
import { isHighPriority } from '../api/task-priority';
import './FavoritesMenu.css';

const CATEGORY_LABEL: Record<string, string> = {
  patrol: 'Patrol',
  station_docking: 'Station',
  delivery: 'Delivery',
  // The `dustbag` category's name since 2026-10-22.
  dustbag: 'Routine check',
  custom: 'Custom',
};

/** What a favourite dispatches, as the form named it: a clean by its
 *  mode ("Scrub"), read from the saved phases -- "Vacuum" for one saved
 *  before modes existed, which is what it still runs as. */
function favoriteLabel(f: Pick<TaskFavorite, 'category' | 'phases'>): string {
  if (f.category === 'clean') return cleanModeLabel(cleanModeOfRequest(f));
  return CATEGORY_LABEL[f.category] ?? f.category;
}

/**
 * Top-bar quick-launch panel for saved task favourites (UI
 * quick-actions request) — visible/runnable from any screen, matching
 * `AlertBell`'s star-icon-and-dropdown pattern already established
 * here rather than a floating widget scoped to the Tasks screen.
 *
 * Running a favourite always confirms first (explicit user choice —
 * dispatching a real task has physical consequences). A future
 * MQTT/IoT-triggered run would call the same `execute` endpoint
 * directly and skip this confirmation, which is fine: the backend
 * endpoint itself has no confirmation step, only this browser UI does.
 */
export function FavoritesMenu() {
  const favorites = useFavoritesV1();
  const executeFavorite = useExecuteFavoriteV1();
  const deleteFavorite = useDeleteFavoriteV1();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [pending, setPending] = useState<TaskFavorite | null>(null);
  const [justRan, setJustRan] = useState<{ name: string; lowered: boolean } | null>(null);
  const canSetHigh = useCanSetHighPriority();

  // THE GUARD USED TO BE `if (isOpen) return`, which is backwards: the
  // listeners existed only while the panel was CLOSED, so it dismissed
  // on neither an outside press nor Escape, on any device. On a phone,
  // where the panel spans the whole bar, re-tapping the trigger was the
  // only way to close it. Now the shared hook, which takes the trigger
  // as `ignore` so pressing it still toggles rather than closing and
  // reopening in one gesture.
  useDismissOnOutside(
    isOpen ? panelRef : NOT_OPEN,
    useCallback(() => setIsOpen(false), []),
    buttonRef,
  );

  useEffect(() => {
    if (!justRan) return undefined;
    const t = window.setTimeout(() => setJustRan(null), 3000);
    return () => window.clearTimeout(t);
  }, [justRan]);

  const list = favorites.data ?? [];

  return (
    <div className="nx-favorites-menu" data-tuck="hide">
      <button
        ref={buttonRef}
        type="button"
        className="nx-favorites-menu__btn"
        onClick={() => setIsOpen((v) => !v)}
        aria-label={`Favourites (${list.length})`}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        data-testid="favorites-menu-btn"
      >
        <span aria-hidden="true">☆</span>
        {list.length > 0 && (
          <span className="nx-favorites-menu__badge" data-testid="favorites-menu-badge">
            {list.length > 99 ? '99+' : list.length}
          </span>
        )}
      </button>
      {isOpen && (
        <div
          ref={panelRef}
          className="nx-favorites-menu__panel"
          role="dialog"
          aria-label="Favourites"
          data-testid="favorites-menu-panel"
        >
          <header className="nx-favorites-menu__head">
            <span>Favourites</span>
          </header>
          {favorites.isLoading ? (
            <div className="nx-favorites-menu__empty">Loading…</div>
          ) : favorites.isError ? (
            <div className="nx-favorites-menu__empty" role="alert">
              Couldn't load favourites.
            </div>
          ) : list.length === 0 ? (
            <div className="nx-favorites-menu__empty">
              No saved favourites yet. Fill out a task on the Tasks tab and click "Save as
              favourite".
            </div>
          ) : (
            <ul className="nx-favorites-menu__list" data-testid="favorites-menu-list">
              {list.map((f) => (
                <li key={f.id} className="nx-favorites-menu__item">
                  <div className="nx-favorites-menu__item-main">
                    <span className="nx-favorites-menu__name">{f.name}</span>
                    <span className="nx-favorites-menu__meta">
                      <span className="nx-favorites-menu__category">
                        {favoriteLabel(f)}
                      </span>
                      {isHighPriority(f.priority) && (
                        <PriorityBadge testId={`favorites-high-${f.id}`} />
                      )}
                      {f.is_shared && (
                        <span
                          className="nx-favorites-menu__shared-badge"
                          title="Visible to everyone on this site"
                        >
                          Shared
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="nx-favorites-menu__item-actions">
                    <button
                      type="button"
                      className="nx-favorites-menu__run-btn"
                      onClick={() => setPending(f)}
                      data-testid={`favorites-run-${f.id}`}
                    >
                      Run
                    </button>
                    <button
                      type="button"
                      className="nx-favorites-menu__delete-btn"
                      onClick={() => deleteFavorite.mutate(f.id)}
                      aria-label={`Delete ${f.name}`}
                      title="Delete favourite"
                      data-testid={`favorites-delete-${f.id}`}
                    >
                      ×
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {justRan && (
            <div
              className="nx-favorites-menu__status"
              role="status"
              data-testid="favorites-ran-status"
            >
              {/* The api-server's word on what went out, not the
                  confirmation's: the role may have changed since. */}
              {justRan.lowered
                ? `Dispatched "${justRan.name}" as Normal. Only a supervisor can send High.`
                : `Dispatched "${justRan.name}".`}
            </div>
          )}
          {executeFavorite.isError && (
            <div className="nx-favorites-menu__status nx-favorites-menu__status--error" role="alert">
              {String((executeFavorite.error as Error)?.message ?? 'Could not run favourite')}
            </div>
          )}
          {deleteFavorite.isError && (
            <div className="nx-favorites-menu__status nx-favorites-menu__status--error" role="alert">
              {String((deleteFavorite.error as Error)?.message ?? 'Could not delete favourite')}
            </div>
          )}
        </div>
      )}
      <ConfirmDialog
        open={pending !== null}
        title={pending ? `Run "${pending.name}" now?` : ''}
        message={
          pending
            ? favoriteRunMessage(favoriteLabel(pending), pending.priority, canSetHigh)
            : undefined
        }
        confirmLabel="Run"
        cancelLabel="Cancel"
        variant="primary"
        onConfirm={() => {
          if (pending) {
            executeFavorite.mutate(pending.id, {
              onSuccess: (res) =>
                setJustRan({ name: pending.name, lowered: res?.priority_lowered === true }),
            });
          }
          setPending(null);
        }}
        onCancel={() => setPending(null)}
      />
    </div>
  );
}
